/**
 * FELVORA — Línea Inmobiliaria: EBR institucional del giro (F5 · Art. 20 · 43ª-IX Bis).
 * Vista para el representante Art. 20 con la MISMA estructura de metodología/niveles que
 * SOFOM (5 factores, ponderaciones, brecha real vs declarado, umbrales), calculada sobre
 * operaciones_inmobiliarias + clientes del giro. Reciclamos el shape de datos y estilo
 * visual usado en la EBR SOFOM, sin tocar sus componentes.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, TrendingUp, TrendingDown, RefreshCw, Building, PenLine, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { giroNombreCorto } from "@/lib/actividad_vulnerable";
import { useMiActividad } from "@/hooks/useMiActividad";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const GRADO_COLOR = {
  "Alto": "bg-red-100 text-red-900",
  "Medio": "bg-amber-100 text-amber-900",
  "Bajo": "bg-emerald-100 text-emerald-900",
  "Sin clasificar": "bg-slate-100 text-slate-700",
};

export default function EbrInmobiliaria() {
  const { user } = useAuth();
  const act = useMiActividad();
  const giroCorto = act.nombreCorto || giroNombreCorto(user?.giro);
  const [meses, setMeses] = useState(12);
  const [ebr, setEbr] = useState(null);
  const [loading, setLoading] = useState(false);
  // Gate de metodología (feb-2026 · iter 07-26). Payload:
  //   { aprobada, ya_opera, debe_bloquear, debe_avisar, version, aprobado_por }
  const [gate, setGate] = useState(null);
  const [aprobando, setAprobando] = useState(false);

  const cargarGate = async () => {
    try {
      const r = await api.get("/riesgo/metodologia/gate-estado");
      setGate(r.data);
    } catch { /* silent — el gate solo se muestra si aplica */ }
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/inmobiliaria/ebr/institucional?meses=${meses}`);
      setEbr(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No autorizado o error");
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); cargarGate(); }, [meses]);

  /**
   * Aprobación de línea base (primera_config) — 1 sola firma admin_entidad.
   * Reusa el mismo endpoint PUT /api/riesgo/metodologia que la EBR SOFOM. El backend detecta
   * estado_edicion="primera_config" y sella la metodología con firmas={admin_entidad}. Al
   * regresar, refresca el gate para que el banner del dashboard y el bloque local desaparezcan.
   * Solo visible al admin_entidad — el analista/lectura ve un mensaje suave.
   */
  const aprobarMetodologia = async () => {
    if (aprobando) return;
    setAprobando(true);
    try {
      // 1) Cargar la metodología vigente (defaults del sistema para línea base).
      const met = await api.get("/riesgo/metodologia").then((r) => r.data);
      // 2) PUT con los mismos factores/umbrales/escalas — el backend sella con 1 firma.
      await api.put("/riesgo/metodologia", {
        factores: met.factores,
        umbrales: met.umbrales,
        escalas: met.escalas,
      });
      toast.success("Metodología EBR aprobada. Tu entidad puede dar de alta prospectos y clientes.");
      await cargarGate();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "No fue posible aprobar la metodología"));
    } finally { setAprobando(false); }
  };

  if (loading || !ebr) return <div className="p-6 text-sm text-slate-500">Cargando EBR institucional del giro…</div>;

  const factores = ebr.factores || [];
  const cli = ebr.agregado?.clientes || {};
  const opx = ebr.agregado?.operaciones || {};

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="ebr-inmo-page">
      {/* feb-2026 · iter 07-26 · Bloque de aprobación de la metodología (línea base).
          Solo visible cuando el gate EBR reporta debe_avisar=true. Resuelve el blocker
          identificado en la auditoría: hasta hoy el usuario Fr. V veía el banner del
          dashboard pero no encontraba dónde aprobar la metodología. La primera versión
          requiere 1 sola firma admin_entidad (backend/riesgo.py:1218-1229). Ocultarlo
          cuando el gate ya está aprobado o cuando el rol del user no puede firmar. */}
      {gate?.debe_avisar && (
        <Card
          data-testid="ebr-inmo-bloque-aprobacion"
          className={gate.debe_bloquear ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}>
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${gate.debe_bloquear ? "bg-red-100" : "bg-amber-100"}`}>
                <AlertTriangle className={`h-5 w-5 ${gate.debe_bloquear ? "text-red-700" : "text-amber-700"}`} />
              </span>
              <div className="flex-1">
                <p className={`font-heading text-base font-semibold ${gate.debe_bloquear ? "text-red-900" : "text-amber-900"}`}>
                  Metodología EBR pendiente de aprobación
                </p>
                <p className={`mt-1 text-sm ${gate.debe_bloquear ? "text-red-800" : "text-amber-800"}`}>
                  {gate.debe_bloquear
                    ? "Tu entidad aún no puede dar de alta prospectos ni clientes hasta que el responsable de cumplimiento apruebe la línea base de la metodología."
                    : "Tu entidad ya opera, pero es obligatorio que el responsable de cumplimiento apruebe la línea base de la metodología."}
                </p>
                <p className="mt-2 text-xs text-slate-600">
                  Fundamento: LFPIORPI Art. 18 fracción VII (Evaluación Basada en Riesgos).
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {user?.role === "admin_entidad" ? (
                    <Button
                      data-testid="ebr-inmo-aprobar-btn"
                      onClick={aprobarMetodologia}
                      disabled={aprobando}
                      className="bg-emerald-600 text-white hover:bg-emerald-700">
                      {aprobando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PenLine className="mr-1.5 h-4 w-4" />}
                      Aprobar metodología (línea base)
                    </Button>
                  ) : (
                    <p className="text-xs italic text-slate-500" data-testid="ebr-inmo-aprobar-restringido">
                      Solo el admin_entidad puede sellar la línea base inicial. Tu rol actual: {user?.role || "—"}.
                    </p>
                  )}
                  <span className="text-xs text-slate-500">
                    Una sola firma. Podrás editar y revisar la metodología después.
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <Building className="mr-2 inline h-6 w-6 text-emerald-700" />
            EBR institucional · {giroCorto}
          </h1>
          <p className="text-sm text-slate-500">
            Datos reales del giro (últimos {ebr.meses} meses) · metodología por entidad · LFPIORPI Art. 20 · 43ª-IX Bis
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(meses)} onValueChange={(v) => setMeses(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="ebr-meses"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[6, 12, 18, 24].map((m) => <SelectItem key={m} value={String(m)}>{m} meses</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={cargar} data-testid="btn-refresh-ebr">
            <RefreshCw className="mr-1 h-4 w-4" /> Actualizar
          </Button>
        </div>
      </div>

      {/* Cabecera scores */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Riesgo REAL institucional</p>
            <p className="text-3xl font-bold text-primary" data-testid="ebr-total-real">{ebr.total_real}</p>
            <Badge className={GRADO_COLOR[ebr.grado_real] || GRADO_COLOR["Sin clasificar"]}>{ebr.grado_real}</Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Riesgo DECLARADO (promedio cédulas)</p>
            <p className="text-3xl font-bold text-slate-700" data-testid="ebr-total-declarado">{ebr.total_declarado}</p>
            <Badge className={GRADO_COLOR[ebr.grado_declarado] || GRADO_COLOR["Sin clasificar"]}>{ebr.grado_declarado}</Badge>
          </CardContent>
        </Card>
        <Card className={ebr.alerta_desviacion ? "border-red-300 bg-red-50" : ""}>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Brecha (real - declarado)</p>
            <p className="text-3xl font-bold flex items-center gap-2" data-testid="ebr-brecha">
              {ebr.desviacion_puntos >= 0
                ? <TrendingUp className="h-6 w-6 text-red-700" />
                : <TrendingDown className="h-6 w-6 text-emerald-700" />}
              {ebr.desviacion_puntos > 0 ? "+" : ""}{ebr.desviacion_puntos}
            </p>
            {ebr.alerta_desviacion && (
              <p className="text-xs text-red-700"><AlertTriangle className="inline h-3 w-3 mr-1" />Alerta de desviación</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Factores */}
      <Card>
        <CardHeader><CardTitle className="text-base">Factores del EBR institucional (5 factores ponderados)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {factores.map((f) => (
            <div key={f.clave} className="border-b border-slate-100 py-2" data-testid={`ebr-factor-${f.clave}`}>
              <div className="flex justify-between text-sm">
                <span className="font-semibold">{f.nombre}</span>
                <span className={f.eleva ? "text-red-700 font-semibold" : "text-slate-700"}>
                  {f.score_real}/100 · ponderación {f.ponderacion}%
                </span>
              </div>
              <div className="h-2 rounded bg-slate-100">
                <div
                  className={`h-2 rounded ${f.eleva ? "bg-red-500" : "bg-emerald-500"}`}
                  style={{ width: `${f.score_real}%` }}
                />
              </div>
              {f.eleva && <p className="text-xs text-red-700 mt-1"><AlertTriangle className="inline h-3 w-3 mr-1" />Factor que eleva el riesgo institucional</p>}
            </div>
          ))}
          <p className="mt-3 text-xs text-slate-500">
            Metodología {ebr.metodologia_version || "default"} · umbrales {JSON.stringify(ebr.umbrales)}
          </p>
        </CardContent>
      </Card>

      {/* Datos reales del giro */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Clientes del giro</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p>Total: <strong>{cli.total}</strong> · PF: {cli.personas_fisicas} · PM: {cli.personas_morales}</p>
            <p>PEP: {cli.pep} · Extranjeros: {cli.extranjeros} · Bloqueados LPB: {cli.bloqueados_lpb}</p>
            <p>Por grado:
              <span className="ml-1"><Badge className={GRADO_COLOR["Alto"]}>Alto: {cli.por_grado?.Alto || 0}</Badge></span>
              <span className="ml-1"><Badge className={GRADO_COLOR["Medio"]}>Medio: {cli.por_grado?.Medio || 0}</Badge></span>
              <span className="ml-1"><Badge className={GRADO_COLOR["Bajo"]}>Bajo: {cli.por_grado?.Bajo || 0}</Badge></span>
              <span className="ml-1"><Badge className={GRADO_COLOR["Sin clasificar"]}>Sin: {cli.por_grado?.["Sin clasificar"] || 0}</Badge></span>
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Operaciones del giro ({ebr.meses}m)</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p>Total: <strong>{opx.total}</strong> · monto <strong>{mxn(opx.monto_total)}</strong></p>
            {(act.fracciones.length ? act.fracciones : ["V"]).map((fr) => (
              <p key={fr}>Fr. {fr}: {opx.por_fraccion?.[fr.replace(/ Bis$/i, "_bis")] || 0}</p>
            ))}
            <p>Efectivo (num/monto): {opx.efectivo_num} / <strong>{mxn(opx.efectivo_monto)}</strong></p>
            <p>Reportables SPPLD: <strong>{opx.reportables || 0}</strong></p>
          </CardContent>
        </Card>
      </div>

      {/* Catálogos parametrizables */}
      <Card>
        <CardHeader><CardTitle className="text-base">Catálogos parametrizables del giro (editables por la entidad)</CardTitle></CardHeader>
        <CardContent className="text-sm">
          <p className="text-xs text-slate-500 mb-2">
            Productos de alto riesgo (pld_config.productos_alto_riesgo) — se editan en el módulo PLD/Riesgo existente:
          </p>
          <div className="flex flex-wrap gap-1">
            {(ebr.params?.productos_alto_riesgo || []).length === 0 && (
              <span className="italic text-slate-400">Sin catálogo definido</span>
            )}
            {(ebr.params?.productos_alto_riesgo || []).map((p) => (
              <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
